import React, { type JSX, useEffect, useRef, useState } from 'react';
import styles from './search.module.scss';
import { Button } from '../Button/Button';
import { Icon } from '../Icon/Icon';
import ReactDOM from 'react-dom';
import { Status } from '../Status/Status';
import { Popover } from '../Popover/Popover';
import { MultiSelect, type MultiSelectProps } from 'primereact/multiselect';
import { Checkbox } from '../Checkbox/Checkbox';
import { DatePicker } from '../DatePicker/Datepicker';
import StateAll from '../../../assets/Status/Status/Status Icon=All, Type=Status.svg';
import { ToggleSwitch } from '../ToggleSwitch/ToggleSwitch';
import type { StatusIcon } from '../Status/status.config';

/**
 * filter for the Search component
 */
export interface AppliedFilter {
  label: string;
  type?: 'dropdown' | 'checkbox' | 'date';
  value: string[];
}

/**
 * Props for the Search component
 */
export interface SearchProps {
  className?: string;
  behavior?: 'Dynamic Suggestions' | 'Selection' | 'Responsiveness';
  items?: string[];
  placeholder?: string;
  filter?: boolean;
  filterState?: 'Auto' | 'Full Width';
  filterButtons?: React.ReactNode;
  filters?: {
    label: string;
    options: { name: string; code: string }[];
    optionLabel: string;
    model: string[];
    showInSearch?: boolean;
    type?: 'dropdown' | 'checkbox' | 'date';
  }[];

  fullWidthFilters?: {
    label: string;
    options: { name: string; code: string }[];
    optionLabel: string;
    model: string[];
    showInSearch?: boolean;
    type?: 'dropdown' | 'checkbox' | 'date';
  }[];
  onFilterChange?: (filters: AppliedFilter[]) => void;
  onSelectionChange?: (values: string[]) => void;
  onQueryChange?: (value: string) => void;
  dropdownAppendTo?: 'self' | 'body' | HTMLElement | null;
  useFixedPosition?: boolean;
  multiSelectProps?: Partial<MultiSelectProps>;
}

export type SearchFilterConfig = NonNullable<SearchProps['filters']>[number];
type SearchFilterState = SearchFilterConfig & { tempModel: string[] };

const normalizeFilterState = (sourceFilters?: SearchFilterConfig[]): SearchFilterState[] =>
  sourceFilters?.map((filter) => ({
    ...filter,
    model: filter.model || [],
    tempModel: filter.model || [],
  })) || [];

/**
 * Filter content props interface
 */
export interface FilterContentProps {
  tempStatuses: string[];
  filterState: 'Auto' | 'Full Width';
  //selectedFolders: string[];
  onStatusToggle?: (status: string, checked: boolean) => void;
  onResetFilters?: () => void;
  onSubmitFilters?: () => void;
  onSelectionChange?: (values: string[]) => void;
}

/**
 * Inline value type for rendering filter values
 */
type InlineValue = string | Record<string, string>;

/**
 * MultiSelect option interface
 */
interface MultiSelectOption {
  name: string;
  code: string;
}

/**
 * Search component with optional filters and suggestions.
 *
 * ## Import path
 * ```tsx
 * import { Search } from '@kpmg-global-audit-emu/react-ui-kit/components/Search';
 * ```
 * @param root0 - Search component props
 * @param root0.behavior - Behavior type for search
 * @param root0.items - List of searchable items
 * @param root0.filter - Whether to show filter
 * @param root0.filterButtons - Custom filter buttons
 * @param root0.filterState - Filter state type
 * @param root0.filters - Filter configuration
 * @param root0.fullWidthFilters - Full width filter configuration
 * @param root0.onFilterChange - Callback for filter changes
 * @param root0.onSelectionChange - Callback for selection changes
 * @param root0.dropdownAppendTo - this is for append to in multiselect
 * @param root0.useFixedPosition - this is for append to in multiselect
 * @param root0.multiSelectProps - Additional props to pass to the underlying PrimeReact MultiSelect component.
 * @returns {JSX.Element} The rendered Search component.
 */
export const Search = ({
  className,
  behavior = 'Dynamic Suggestions',
  items = [],
  placeholder,
  filter = false,
  filterButtons,
  filterState,
  filters,
  fullWidthFilters,
  onFilterChange,
  onSelectionChange,
  onQueryChange,
  dropdownAppendTo = null,
  useFixedPosition = false,
  multiSelectProps,
}: SearchProps): JSX.Element => {
  // --- Basic search states ---
  // const [showSearch, setShowSearch] = useState(false);
  const [showSearch, setShowSearch] = useState(filterState === 'Full Width');

  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [chips, setChips] = useState<string[]>([]);
  const [showFilter, setShowFilter] = useState(false);
  const [selectedFolders, setSelectedFolders] = useState<string[]>([]);
  const [hideInlineFilters, setHideInlineFilters] = useState(true);
  const searchContainerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [containerLeft, setContainerLeft] = useState(0);
  const isInitialMount = useRef(true);

  /**
   * Temporary state map for full width filters.
   * Key = filter label, Value = selected values
   */
  type TempFullWidthState = Record<string, string[]>;

  const [tempFullWidth, setTempFullWidth] = useState<TempFullWidthState>({});
  const [filtersState, setFiltersState] = useState<SearchFilterState[]>(() => {
    const sourceFilters = filterState === 'Full Width' ? fullWidthFilters : filters;
    return normalizeFilterState(sourceFilters);
  });
  const visibleSearchFilters = filtersState.filter((f) => f.showInSearch);
  // --- Refs + position for suggestions ---
  const inputRef = useRef<HTMLDivElement | null>(null);
  const inputInnerRef = useRef<HTMLInputElement | null>(null);
  const [popoverVisible, setPopoverVisible] = useState(false);
  // const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });

  /**
   * Returns visible items and hidden count based on limit.
   * @param {string[] | undefined} list - List of values
   * @param {number} limit - Max visible items
   * @returns {{ visible: string[]; hiddenCount: number }}
   */
  const getVisibleAndHidden = (list: string[] | undefined, limit = 1) => {
    const safeList = Array.isArray(list) ? list : [];
    const visible = safeList.slice(0, limit);
    const hiddenCount = safeList.length > limit ? safeList.length - limit : 0;
    return { visible, hiddenCount };
  };

  //code commit 8dec

  getVisibleAndHidden(selectedFolders);
  useEffect(() => {
    //setShowFilter(false);

    if (filterState === 'Full Width') {
      setShowSearch(true);
      // Initialize with fullWidthFilters
      setFiltersState(
        normalizeFilterState(fullWidthFilters).map((filter) => ({
          ...filter,
          model: [],
          tempModel: [],
        }))
      );
    } else {
      setShowSearch(false);
      setHideInlineFilters(true);
      // Initialize with filters
      setFiltersState(
        normalizeFilterState(filters).map((filter) => ({
          ...filter,
          model: [],
          tempModel: [],
        }))
      );
    }

    setQuery('');
  onQueryChange?.('');
    setSuggestions([]);
    setChips([]);
    setSelectedFolders([]);
    setPopoverVisible(false);
  }, [filterState]);
  //filters, fullWidthFilters

  // 1. Run only on initial mount
  useEffect(() => {
    if (!isInitialMount.current) return;
    isInitialMount.current = false;

    setShowFilter(false);
    if (filterState === 'Full Width') {
      setShowSearch(true);
    } else {
      setShowSearch(false);
      setHideInlineFilters(true);
    }
  }, []); // Empty deps - runs once

  // 2. Sync filters from parent WITHOUT resetting UI
  useEffect(() => {
    if (isInitialMount.current) return; // Skip initial mount

    const sourceFilters = filterState === 'Full Width' ? fullWidthFilters : filters;

    setFiltersState((prev) => {
      // Create new state from source filters
      const newFilters =
        sourceFilters?.map((sourceFilter) => {
          // Find if this filter exists in current state
          const existingFilter = prev.find((p) => p.label === sourceFilter.label);

          return {
            ...sourceFilter,
            // Keep existing model if available, otherwise use incoming
            model: existingFilter?.model || sourceFilter.model || [],
            tempModel: existingFilter?.tempModel || sourceFilter.model || [],
          };
        }) || [];

      // Only update if actually different to avoid infinite loops
      const hasChanged =
        JSON.stringify(newFilters.map((f) => ({ label: f.label, model: f.model }))) !==
        JSON.stringify(prev.map((f) => ({ label: f.label, model: f.model })));

      return hasChanged ? newFilters : prev;
    });
  }, [filters, fullWidthFilters, filterState]);
  useEffect(() => {
    if (behavior === 'Selection') {
      setShowSearch(true);
      setSuggestions(items);
      return;
    }

    // Only close search if NOT Full Width
    if (filterState !== 'Full Width') {
      setShowSearch(false);
    }

    setSuggestions([]);
  }, [behavior, items, filterState]);

  useEffect(() => {
    if (showSearch) {
      setHideInlineFilters(true); // reveal inline filters immediately
      setShowFilter(true); // show filter button
    }
    const upd = () => {
      if (inputRef.current) {
        // Placeholder for future inputRef logic
      }
      if (searchContainerRef.current) {
        const containerRect = searchContainerRef.current.getBoundingClientRect();
        setContainerWidth(containerRect.width);
        setContainerLeft(containerRect.left); // No scrollX for fixed positioning
      }
    };
    if (showSearch) upd();
    window.addEventListener('resize', upd);
    window.addEventListener('scroll', upd, true);
    return () => {
      window.removeEventListener('resize', upd);
      window.removeEventListener('scroll', upd, true);
    };
  }, [showSearch]);

  /**
   * Handles search input change and updates suggestions.
   * @param {React.ChangeEvent<HTMLInputElement>} e - Input change event
   * @returns {void}
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    onQueryChange?.(val);
    if (!val.trim()) return setSuggestions([]);
    setSuggestions(items.filter((i) => i.toLowerCase().includes(val.toLowerCase())));
  };

  const handleSelect = (item: string) => {
    const updated = [...chips, item];
    setChips(updated);
    setQuery('');
    setSuggestions([]);
    onSelectionChange?.(updated);
  };

  const handlePopoverClose = () => setPopoverVisible(false);
  const popoverWrapperRef = useRef<HTMLDivElement | null>(null);
  const fullWidthPanelRef = useRef<HTMLDivElement | null>(null);
  const suggestionListRef = useRef<HTMLUListElement | null>(null);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const resolvedDropdownAppendTo = dropdownAppendTo === 'body' ? document.body : dropdownAppendTo;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        popoverWrapperRef.current?.contains(target) ||
        fullWidthPanelRef.current?.contains(target) ||
        suggestionListRef.current?.contains(target)
      ) {
        return;
      }
      setPopoverVisible(false);
    };

    if (popoverVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [popoverVisible]);

  const filterTriggerElement =
    filterButtons && showFilter ? (
      <div
        className={styles.filterbtn}
        role="button"
        tabIndex={0}
        onClick={() => setPopoverVisible(!popoverVisible)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setPopoverVisible(!popoverVisible);
          }
        }}
        onMouseDown={(e) => e.preventDefault()}
      >
        {filterButtons}
      </div>
    ) : showFilter && !hideInlineFilters ? (
      <Button
        buttonPrefix
        buttonType="Tertiary"
        classType="Standard"
        prefixInstance="Filter1"
        size="Small"
        state="Default"
        theme="Rectangular"
        onClick={() => setPopoverVisible(!popoverVisible)}
      >
        Filter
      </Button>
    ) : null;

  /**
   * Renders inline filter text with +N logic.
   * @param {InlineValue[]} values - Selected filter values
   * @param {string} label - Fallback label
   * @param {string} optionLabel - Key to read label from object value
   * @returns {string} Rendered inline text
   */
  const renderInlineValue = (values: InlineValue[], label: string, optionLabel: string) => {
    if (!values || values.length === 0) return label;

    const visible = values
      .slice(0, 2)
      .map((v) => (typeof v === 'string' ? v : v[optionLabel]))
      .join(', ');

    const rest = values.length - 2;

    return rest > 0 ? `${visible} +${rest}` : visible;
  };

  const prevPopoverVisible = useRef(popoverVisible);
  useEffect(() => {
    if (popoverVisible && !prevPopoverVisible.current) {
      const initial: TempFullWidthState = {};

      filtersState.forEach((f) => {
        initial[f.label] = f.model ?? [];
      });

      setTempFullWidth(initial);
    }

    prevPopoverVisible.current = popoverVisible;
  }, [filtersState, popoverVisible]);

  const wasInputFocused = useRef(false);
  useEffect(() => {
    if (wasInputFocused.current) {
      requestAnimationFrame(() => {
        inputInnerRef.current?.focus();
      });
    }
  }, [filtersState, tempFullWidth, popoverVisible]);

  const isSubmitDisabled = React.useMemo(() => {
    if (!fullWidthFilters) return true;

    const hasAnyValue = Object.entries(tempFullWidth).some(([label, arr]) => {
      const filter = fullWidthFilters.find((f) => f.label === label);
      if (!filter) return false;

      if (filter.type === 'date') {
        return Array.isArray(arr) && arr.length > 0 && arr[0].trim() !== '';
      }

      return Array.isArray(arr) && arr.length > 0;
    });

    return !hasAnyValue;
  }, [tempFullWidth, fullWidthFilters]);

  const emitFilterChange = React.useCallback(
    (state = filtersState) => {
      if (!onFilterChange) return;

      const applied = state
        .filter((f) => f.model && f.model.length > 0)
        .map((f) => ({
          label: f.label,
          type: f.type,
          value: f.model,
        }));

      onFilterChange(applied);
    },
    [filtersState, onFilterChange]
  );
  /**
   * Renders full width filter popover content.
   * @returns {JSX.Element | null} Popover JSX
   */
  const fullwidthPopver = () => {
    if (!fullWidthFilters) return null;

    return (
      <>
        <div
          className={styles.filtercont}
          style={{ width: '100%', maxWidth: '100%' }}
          ref={fullWidthPanelRef}
        >
          {fullWidthFilters.map((filter) => {
            const value = tempFullWidth[filter.label] || [];
            if (filter.type === 'dropdown') {
              return (
                <div key={filter.label} className="w-full">
                  <label style={{ display: 'block', marginBottom: 4 }}>{filter.label}</label>

                  <div style={{ position: 'relative' }}>
                    <MultiSelect
                      {...multiSelectProps}
                      value={value}
                      onChange={(e) =>
                        setTempFullWidth((prev) => ({
                          ...prev,
                          [filter.label]: e.value,
                        }))
                      }
                      options={[{ name: 'All', code: '__ALL__' }, ...filter.options]}
                      optionLabel={filter.optionLabel}
                      optionValue="code"
                      placeholder={`Select ${filter.label}`}
                      className={`w-full ${multiSelectProps?.className ?? ''}`.trim()}
                      showSelectAll={false}
                      onShow={() => setOpenDropdown(filter.label)}
                      onHide={() => setOpenDropdown(null)}
                      dropdownIcon={
                        <Icon
                          name={openDropdown === filter.label ? 'Chevron Up' : 'Chevron Down'}
                          shape="Square"
                          size="large"
                          type="Base Icon"
                          variant="Outline"
                        />
                      }
                      itemTemplate={(option) =>
                        renderMultiSelectItem(option, {
                          value: value,
                          allOptions: filter.options,
                          onChange: (newValue) =>
                            setTempFullWidth((prev) => ({
                              ...prev,
                              [filter.label]: newValue,
                            })),
                        })
                      }
                    />

                    <span
                      className={`${styles.inlineText} ${value && value.length > 2 ? styles.hasSpanMore : ''
                        }`}
                    >
                      {renderInlineValue(value, filter.label, filter.optionLabel)}
                    </span>
                  </div>
                </div>
              );
            }

            if (filter.type === 'date') {
              const value = tempFullWidth[filter.label]?.[0];

              return (
                <div key={filter.label} className="w-full">
                  <DatePicker
                    datePickerType="Range"
                    states="Default"
                    label={filter.label}
                    placeholder="Mmm DD, YYYY to Mmm DD, YYYY"
                    value={
                      value
                        ? [new Date(value.split(' - ')[0]), new Date(value.split(' - ')[1])]
                        : null
                    }
                    onChange={(range) => {
                      if (!Array.isArray(range) || !range[0] || !range[1]) {
                        // Clear value if incomplete
                        setTempFullWidth((prev) => ({
                          ...prev,
                          [filter.label]: [],
                        }));
                        return;
                      }

                      const formatted = `${range[0].toDateString()} - ${range[1].toDateString()}`;
                      setTempFullWidth((prev) => ({
                        ...prev,
                        [filter.label]: [formatted],
                      }));
                    }}
                  />
                </div>
              );
            }

            if (filter.type === 'checkbox') {
              const allOptions = filter.options.map((o) => o.name);
              const isAllSelected =
                allOptions.length > 0 && allOptions.every((o) => value.includes(o));
              return (
                <div key={filter.label} className={styles.statusFilter}>
                  <label style={{ display: 'block', marginBottom: 4 }}>{filter.label}</label>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Checkbox
                      checkboxId="checkbox-all"
                      size="Small"
                      state="default"
                      checked={isAllSelected}
                      onChange={(checked) =>
                        setTempFullWidth((prev) => ({
                          ...prev,
                          [filter.label]: checked ? allOptions : [],
                        }))
                      }
                    />
                    <img className={styles.allicon} src={StateAll} alt="All Statuses" />
                    <span style={{ fontSize: 12, fontWeight: 600 }}>All Statuses</span>
                  </div>

                  {filter.options.map((status) => (
                    <div
                      className={styles.allstatus}
                      key={status.name}
                      style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                    >
                      <Checkbox
                        checkboxId={`checkbox-${status.name}`}
                        size="Small"
                        state="default"
                        checked={value.includes(status.name)}
                        onChange={(checked) =>
                          setTempFullWidth((prev) => {
                            const current = prev[filter.label] || [];
                            return {
                              ...prev,
                              [filter.label]: checked
                                ? [...current, status.name]
                                : current.filter((x) => x !== status.name),
                            };
                          })
                        }
                      />
                      <Status
                        labelAlignment="right"
                        size="Small"
                        icon={status.name as StatusIcon}
                      />
                    </div>
                  ))}
                </div>
              );
            }

            return null;
          })}
        </div>
        <div className={styles.filterfooter}>
          <Popover
            content={
              <div>
                <div>
                  <Button
                    buttonPrefix
                    buttonType="Tertiary"
                    classType="Standard"
                    prefixInstance="Bookmark"
                    size="Small"
                    state="Default"
                    theme="Rectangular"
                  />
                  <div>Bookmark</div>
                </div>
                <div>
                  <Button
                    buttonPrefix
                    buttonType="Tertiary"
                    classType="Standard"
                    prefixInstance="Upload"
                    size="Small"
                    state="Default"
                    theme="Rectangular"
                  />
                  <div>Share</div>
                </div>
                <div>
                  <Button
                    buttonPrefix
                    buttonType="Tertiary"
                    classType="Standard"
                    prefixInstance="Settings"
                    size="Small"
                    state="Default"
                    theme="Rectangular"
                  />
                  <div>Preferences</div>
                </div>
              </div>
            }
            id="popover-id"
            kebabMenu
            positioning="top-left"
            triggerElement={
              <Icon
                name="Kebab Menu Vertical"
                shape="Circle"
                size="Medium"
                type="Base Icon"
                variant="Filled"
              />
            }
          />
          <div className={styles.btngroupspacer}>
            <Button
              buttonType="Tertiary"
              classType="Standard"
              label="Reset"
              state="Default"
              onClick={() => {
                const cleared = filtersState.map((f) => ({
                  ...f,
                  model: [],
                }));

                emitFilterChange([]);
                setFiltersState(cleared);
                setTempFullWidth(
                  cleared.reduce((acc, f) => {
                    acc[f.label] = [];
                    return acc;
                  }, {} as TempFullWidthState)
                );

                setHideInlineFilters(true);
                setPopoverVisible(false);
              }}
              size="Medium"
              text
            />
            <Button
              buttonType="Primary"
              classType="Standard"
              label="Submit"
              size="Medium"
              state="Default"
              text
              disabled={isSubmitDisabled}
              onClick={() => {
                const updated: SearchFilterState[] = fullWidthFilters.map((f) => ({
                  ...f,
                  model: tempFullWidth[f.label] || [],
                  tempModel: tempFullWidth[f.label] || [],
                }));

                setFiltersState(updated);
                emitFilterChange(updated);
                setTempFullWidth(
                  updated.reduce((acc, f) => {
                    acc[f.label] = f.model;
                    return acc;
                  }, {} as TempFullWidthState)
                );

                setHideInlineFilters(true);
                setPopoverVisible(false);
              }}
            />
          </div>
        </div>
      </>
    );
  };

  /**
   * Custom renderer for MultiSelect checkbox items.
   * @param {MultiSelectOption} option - Option data
   * @param {object} props - Render props
   * @param {string[]} props.value - Selected values
   * @param {(value: string[]) => void} props.onChange - Change handler
   * @param {{ name: string; code: string }[]} [props.allOptions] - All options list
   * @returns {JSX.Element} Rendered option
   */
  const renderMultiSelectItem = (
    option: MultiSelectOption,
    props: {
      value: string[];
      onChange: (value: string[]) => void;
      allOptions?: { name: string; code: string }[];
    }
  ) => {
    const isAll = option.code === '__ALL__';

    const checked = isAll
      ? !!props.allOptions?.every((o) => props.value.includes(o.code))
      : props.value.includes(option.code);

    const handleClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      e.stopPropagation();

      let newValue: string[] = [];

      if (isAll) {
        newValue = checked ? [] : (props.allOptions?.map((o) => o.code) ?? []);
      } else {
        newValue = checked
          ? props.value.filter((v) => v !== option.code)
          : [...props.value, option.code];
      }

      props.onChange(newValue);
    };
    return (
      <div
        className={styles.customCheckboxOption}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick(e as any);
          }
        }}
        onClick={handleClick}
        onMouseDown={(e) => e.preventDefault()}
      >
        <span className={styles.optionLabel}>{option.name}</span>
        <ToggleSwitch
          label="Text Label"
          name="toggle-switch"
          onChange={() => {
            if (isAll) {
              props.onChange(checked ? [] : (props.allOptions?.map((o) => o.code) ?? []));
            } else {
              props.onChange(
                checked
                  ? props.value.filter((v) => v !== option.code)
                  : [...props.value, option.code]
              );
            }
          }}
          state={checked}
          toggleSwitchId="toggle-switch-id"
        />
      </div>
    );
  };

  const statusFilter = filtersState.find(
    (f) => f.type === 'checkbox' && f.model && f.model.length > 0
  );

  const appliedFilters = filtersState.filter((f) => f.model && f.model.length > 0);

  const onlyDateSelected = appliedFilters.length === 1 && appliedFilters[0].type === 'date';

  const showPlusCount = !onlyDateSelected && appliedFilters.length > 0;

  const plusCount = statusFilter ? appliedFilters.length - 1 : null;

  return (
    <div
      ref={searchContainerRef}
      className={`${styles.searchContainer} ${!filter ? styles.noFilter : ''} ${behavior === 'Responsiveness' ? styles.responsive : ''} ${filterState === 'Auto' ? styles.autoContainer : styles.fullwidthContainer} ${className ?? ''}`.trim()}
      tabIndex={-1}
      onFocus={() => {
        setHideInlineFilters(true);
        setShowFilter(true);
      }}
      role="presentation"
    >
      {!showSearch && (
        <div className={styles.searchButtonWrapper}>
          <Button
            state="Default"
            buttonPrefix
            buttonType="Tertiary"
            classType="Standard"
            prefixInstance="Search"
            size="Medium"
            onClick={() => {
              setShowSearch(true);
              setTimeout(() => {
                inputInnerRef.current?.focus();
              }, 0);
            }}
          />
        </div>
      )}

      {showSearch && (
        <div
          className={`${styles.searchRow} ${popoverVisible ? styles.inputActivePopover : ''} ${behavior === 'Selection' ? styles.selectionBehavior : ''}`}
        >
          {/* Left search input */}
          <div
            className={styles.searchLeftBox}
            ref={inputRef}
            role="searchbox"
            tabIndex={0}
            onClick={() => inputInnerRef.current?.focus()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                inputInnerRef.current?.focus();
              }
            }}
            onMouseDown={(e) => e.preventDefault()}
          >
            <div className={styles.iconWrapper}>
              <Icon name="Search" shape="Square" size="Medium" type="Base Icon" variant="Outline" />
            </div>
            <div className={styles.chipInputBox}>
              {placeholder &&
                chips.length === 0 &&
                query.trim().length === 0 && (
                  <span
                    className={styles.searchPlaceholder}
                    aria-hidden="true"
                  >
                    {placeholder}
                  </span>
                )
              }

              {chips.slice(0, 3).map((chip, i) => (
                <div key={chip} className={styles.chipInside}>
                  <span className={styles.chipInputtext}>{chip}</span>

                  <Button
                    buttonPrefix
                    buttonType="Tertiary"
                    classType="Standard"
                    onClick={(e) => {
                      e.stopPropagation();

                      const updated = chips.filter((_, idx) => idx !== i);
                      setChips(updated);
                      onSelectionChange?.(updated);

                      if (updated.length === 0) {
                        setSuggestions([]);
                        setQuery('');
                        onQueryChange?.('');
                      }
                    }}
                    prefixInstance="Close"
                    size="Small"
                    state="Default"
                    theme="Rectangular"
                  />
                </div>
              ))}

              {chips.length > 3 && (
                <div className={`${styles.chipInside} ${styles.chipPluse}`}>
                  +{chips.length - 3}
                </div>
              )}

              <input
                ref={inputInnerRef}
                value={query}
                aria-label={placeholder || 'Search'}
                placeholder={placeholder}
                onChange={handleChange}
                className={styles.inputField}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && query.trim()) {
                    setChips((p) => [...p, query]);
                    setQuery('');
                  }
                }}
                onFocus={() => {
                  setHideInlineFilters(false);
                  setShowFilter(true);

                  if (behavior === 'Selection') {
                    setSuggestions(items);
                  } else {
                    setSuggestions([]);
                  }
                }}
              />
            </div>
          </div>

          {/* Auto filters */}
          {filter && filterState === 'Full Width' && (
            <div
              className={`${styles.searchRightBox} ${popoverVisible ? styles.inputActivePopover : ''}`}
            >
              {onlyDateSelected && (
                <div className={styles.selectedStatusDisplay}>
                  {appliedFilters[0].model.map((d) => (
                    <span key={d} className={styles.dateBadge}>
                      {d}
                    </span>
                  ))}
                </div>
              )}

              {statusFilter && (
                <div className={styles.selectedStatusDisplay}>
                  {statusFilter.model.slice(0, 3).map((s) => (
                    <Status key={s} labelAlignment="right" size="Small" icon={s as StatusIcon} />
                  ))}

                  {statusFilter.model.length > 3 && (
                    <div className={styles.moreCount}>
                      +
                      {statusFilter.model.length - 3 + (showPlusCount && plusCount ? plusCount : 0)}
                    </div>
                  )}
                </div>
              )}

              {/* Badges */}
              <div
                style={{
                  display: 'flex',
                  gap: '0.5rem',
                  marginBottom: '0.5rem',
                  alignItems: 'center',
                }}
              >
                {!statusFilter &&
                  fullWidthFilters
                    ?.filter((f) => f.type === 'dropdown' && f.showInSearch === true)
                    .slice(0, 2)
                    .map((f) => {
                      // Find the current model value from filtersState or use empty array
                      const stateFilter = filtersState.find((sf) => sf.label === f.label);
                      const currentModel = stateFilter?.model || [];

                      return (
                        <div key={f.label} className={styles.inlineWrapper}>
                          <MultiSelect
                            {...multiSelectProps}
                            value={currentModel}
                            options={[{ name: 'All', code: '__ALL__' }, ...f.options]}
                            optionLabel={f.optionLabel}
                            optionValue="code"
                            onChange={(e) => {
                              const newState = [...filtersState];
                              const index = newState.findIndex((x) => x.label === f.label);

                              if (index !== -1) {
                                // Update existing filter
                                newState[index] = { ...newState[index], model: e.value };
                              } else {
                                // Add new filter if not found
                                newState.push({
                                  ...f,
                                  model: e.value,
                                  tempModel: e.value,
                                });
                              }

                              setFiltersState(newState);
                              emitFilterChange(newState);
                            }}
                            placeholder={f.label}
                            className={`${styles.inlineMultiSelect} ${multiSelectProps?.className ?? ''}`.trim()}
                            showClear={false}
                            onShow={() => setOpenDropdown(f.label)}
                            onHide={() => setOpenDropdown(null)}
                            dropdownIcon={
                              <Icon
                                name={openDropdown === f.label ? 'Chevron Up' : 'Chevron Down'}
                                shape="Square"
                                size="Medium"
                                type="Base Icon"
                                variant="Outline"
                              />
                            }
                            showSelectAll={false}
                            itemTemplate={(option) =>
                              renderMultiSelectItem(option, {
                                value: currentModel,
                                allOptions: f.options,
                                onChange: (newValue) => {
                                  const newState = [...filtersState];
                                  const index = newState.findIndex((x) => x.label === f.label);

                                  if (index !== -1) {
                                    newState[index] = {
                                      ...newState[index],
                                      model: newValue,
                                    };
                                  } else {
                                    newState.push({
                                      ...f,
                                      model: newValue,
                                      tempModel: newValue,
                                    });
                                  }

                                  setFiltersState(newState);
                                  // emitFilterChange(newState);
                                  requestAnimationFrame(() => {
                                    emitFilterChange(newState);
                                  });
                                },
                              })
                            }
                            appendTo={resolvedDropdownAppendTo}
                            panelStyle={{
                              position: 'fixed', // Ensure panel uses fixed positioning
                            }}
                          />

                          <span
                            className={`${styles.inlineText} ${currentModel && currentModel.length > 2 ? styles.hasSpanMore : ''
                              }`}
                          >
                            {renderInlineValue(currentModel, f.label, f.optionLabel)}
                          </span>
                        </div>
                      );
                    })}
              </div>

              {/* Popover */}
              {showFilter && (
                <div ref={popoverWrapperRef} className={styles.filterButtonInside}>
                  <Popover
                    actions={null}
                    content={fullwidthPopver()}
                    header={<div className={styles.filterheader}>Filters</div>}
                    id="search-filter-popover"
                    positioning="bottom-right"
                    useFixedPosition={useFixedPosition}
                    showActions={false}
                    showCloseIcon
                    showHeader
                    triggerElement={filterTriggerElement}
                    popoverCustomClass={styles.searchFilterPopover}
                    overlayPanelProps={{ onHide: handlePopoverClose }}
                  />
                </div>
              )}
            </div>
          )}

          {/* Full Width filters */}
          {filter && filterState === 'Auto' && (
            <div
              className={`${styles.searchRightBox} ${popoverVisible ? styles.inputActivePopover : ''}`}
              style={{ position: 'relative' }}
            >
              {/* -------- Inline applied filters (max 2) -------- */}
              {(() => {
                const dateFilter = filtersState.find(
                  (f) => f.type === 'date' && f.model.length > 0
                );

                const otherFilters = visibleSearchFilters.filter(
                  (f) => f.type !== 'date' && f.model.length > 0
                );

                const inlineFilters = dateFilter
                  ? [dateFilter, ...otherFilters.slice(0, 2)]
                  : otherFilters.slice(0, 3);

                const appliedFilters = filtersState.filter((f) => f.model && f.model.length > 0);
                const hiddenCount = appliedFilters.length - inlineFilters.length;

                return (
                  <div
                    style={{
                      display: 'flex',
                      gap: '0.5rem',
                      marginBottom: '0.5rem',
                      alignItems: 'center',
                    }}
                  >
                    {/* hideInlineFilters && */}
                    {visibleSearchFilters.slice(0, 3).map((f) => {
                      const originalIndex = filtersState.findIndex((x) => x.label === f.label);
                      if (f.type === 'dropdown') {
                        return (
                          <div key={`inline-${f.label}`} className={`${styles.inlineWrapper}`}>
                            <MultiSelect
                              {...multiSelectProps}
                              value={f.model}
                              options={[{ name: 'All', code: '__ALL__' }, ...f.options]}
                              optionLabel={f.optionLabel}
                              optionValue="code"
                              onChange={(e) => {
                                const newState = [...filtersState];
                                newState[originalIndex] = {
                                  ...newState[originalIndex],
                                  model: e.value,
                                };
                                setFiltersState(newState);
                              }}
                              placeholder={f.label}
                              className={`${styles.inlineMultiSelect} ${multiSelectProps?.className ?? ''}`.trim()}
                              showClear={false}
                              onShow={() => setOpenDropdown(f.label)}
                              onHide={() => setOpenDropdown(null)}
                              dropdownIcon={
                                <Icon
                                  name={openDropdown === f.label ? 'Chevron Up' : 'Chevron Down'}
                                  shape="Square"
                                  size="Medium"
                                  type="Base Icon"
                                  variant="Outline"
                                />
                              }
                              showSelectAll={false}
                              itemTemplate={(option) =>
                                renderMultiSelectItem(option, {
                                  value: f.model,
                                  allOptions: f.options,
                                  onChange: (newValue) => {
                                    const newState = [...filtersState];
                                    const index = newState.findIndex((x) => x.label === f.label);

                                    newState[index] = {
                                      ...newState[index],
                                      model: newValue,
                                    };

                                    setFiltersState(newState);
                                  },
                                })
                              }
                            />
                            <span
                              className={`${styles.inlineText} ${f.model && f.model.length > 2 ? styles.hasSpanMore : ''
                                }`}
                            >
                              {renderInlineValue(f.model, f.label, f.optionLabel)}
                            </span>
                          </div>
                        );
                      }

                      if (f.type === 'checkbox') {
                        return (
                          <div key={`inline-${f.label}`} className={styles.selectedStatusDisplay}>
                            {f.model.slice(0, 3).map((s) => (
                              <Status
                                key={s}
                                label
                                labelAlignment="right"
                                size="Small"
                                state={s as React.ComponentProps<typeof Status>['state']}
                              />
                            ))}
                            {f.model.length > 3 && (
                              <div className={styles.moreCount}>+{f.model.length - 3}</div>
                            )}
                          </div>
                        );
                      }
                      if (inlineFilters.length === 0 && f.type === 'date') {
                        return (
                          <div key={`inline-${f.label}`} className={styles.selectedStatusDisplay}>
                            {f.model.slice(0, 2).map((d) => (
                              <span key={d} className={styles.dateBadge}>
                                {d}
                              </span>
                            ))}
                            {f.model.length > 2 && (
                              <div className={styles.moreCount}>+{f.model.length - 2}</div>
                            )}
                          </div>
                        );
                      }

                      return null;
                    })}

                    {hiddenCount > 0 && <div className={styles.moreCount}>+{hiddenCount}</div>}
                  </div>
                );
              })()}

              {/* -------- Filter Button -------- */}
              <div className={styles.filtercloseButtonInside}>
                {filterTriggerElement ?? null}
                {/* {hideInlineFilters && ( */}
                <div className={styles.closeIcon}>
                  <Button
                    buttonType="Tertiary"
                    state="Default"
                    classType="Standard"
                    size="Medium"
                    prefixInstance="Close"
                    buttonPrefix
                    onClick={() => {
                      setFiltersState((prev) => prev.map((f) => ({ ...f, model: [] })));
                      setHideInlineFilters(true);
                      setShowFilter(false);
                      setShowSearch(false);
                      setQuery('');
                      onQueryChange?.('');
                      setChips([]);
                      setPopoverVisible(false);
                      emitFilterChange([]);
                    }}
                  >
                    Close
                  </Button>
                </div>
                {/* )} */}
              </div>

              {/* -------- Full Width Filter Panel -------- */}
              {popoverVisible &&
                ReactDOM.createPortal(
                  <div
                    ref={fullWidthPanelRef}
                    onMouseDown={(e) => e.stopPropagation()}
                    className={styles.fullWidthFilterPanel}
                    style={{
                      position: 'absolute', // Use 'absolute' to scroll with page
                      top: inputRef.current
                        ? `${inputRef.current.getBoundingClientRect().bottom + window.scrollY + 4}px` // Add scrollY offset
                        : '0px',
                      left: inputRef.current
                        ? `${inputRef.current.getBoundingClientRect().left + window.scrollX}px` // Add scrollX offset
                        : '0px',

                      width: containerWidth > 0 ? `${containerWidth}px` : '855px',
                      zIndex: 99,
                    }}
                    role="presentation"
                  >
                    {filtersState.map((f, idx) => (
                      <div key={`panel-${f.label}`} className={styles.filterItem}>
                        <label htmlFor={`filter-${f.label}`}>{f.label}</label>

                        <div className={styles.inlineWrapper}>
                          <MultiSelect
                            {...multiSelectProps}
                            inputId={`filter-${f.label}`}
                            value={f.model}
                            onChange={(e) => {
                              const newState = [...filtersState];
                              newState[idx] = { ...newState[idx], model: e.value };
                              setFiltersState(newState);
                            }}
                            options={[{ name: 'All', code: '__ALL__' }, ...f.options]}
                            optionLabel={f.optionLabel}
                            optionValue="code"
                            placeholder={`Select ${f.label}(s)`}
                            className={`w-full ${multiSelectProps?.className ?? ''}`.trim()}
                            onShow={() => setOpenDropdown(f.label)}
                            onHide={() => setOpenDropdown(null)}
                            dropdownIcon={
                              <Icon
                                name={openDropdown === f.label ? 'Chevron Up' : 'Chevron Down'}
                                shape="Square"
                                size="large"
                                type="Base Icon"
                                variant="Outline"
                              />
                            }
                            showSelectAll={false}
                            itemTemplate={(option) =>
                              renderMultiSelectItem(option, {
                                value: f.model,
                                allOptions: f.options,
                                onChange: (newValue) => {
                                  const newState = [...filtersState];
                                  newState[idx] = { ...newState[idx], model: newValue };
                                  setFiltersState(newState);
                                },
                              })
                            }
                          />

                          <span
                            className={`${styles.inlineText} ${f.model && f.model.length > 2 ? styles.hasSpanMore : ''
                              }`}
                          >
                            {renderInlineValue(f.model, f.label, f.optionLabel)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>,
                  document.body
                )}
            </div>
          )}
        </div>
      )}
      {/* Suggestions portal */}
      {suggestions.length > 0 &&
        showSearch &&
        ReactDOM.createPortal(
          <ul
            ref={suggestionListRef}
            className={`${styles.suggestionList} ${behavior === 'Selection' ? styles.selectionList : ''} searchItem_ul ${filterState === 'Full Width' ? styles.fullWidthList : ''}`}
            style={{
              position: 'absolute',
              top: inputRef.current
                ? `${inputRef.current.getBoundingClientRect().bottom + window.scrollY + 4}px` // Add scrollY offset
                : '0px',
              left: containerLeft ? `${containerLeft}px` : '0px',
              width: containerWidth > 0 ? `${containerWidth}px` : '855px',
              zIndex: 99999,
              backgroundColor: 'var(--surface-elevated)',
              display: 'block',
              visibility: 'visible',
            }}
          >
            {suggestions.map((item, index) => (
              <li key={`${item}-${index}`}>
                <button
                  type="button"
                  className={`${styles.suggestionItem} searchItem_list`}
                  onClick={() => handleSelect(item)}
                >
                  {item}
                </button>
              </li>
            ))}
          </ul>,
          document.body
        )}
    </div>
  );
};
